import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import copy from 'copy-to-clipboard';
import {
  Stack,
  Button,
  Box,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  TableContainer,
  Table,
  Paper,
  Checkbox,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  Card,
  TextField,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import Buckets from '../form-elements/buckets.component';
import {
  cancelTranscodingJob,
  checkFileStatus,
  connectS3Bucket,
  getS3FilesListData,
  processAgainTranscoding,
  transcodeS3Files,
} from '../../services/hls-transcoder.service';
import NoData from '../table-elements/no-data.component';
import Loading from '../table-elements/loading.component';
import { formatTimestamp } from '../../utils/datetime.util';
import { ACTIONS, CDN77_STATUS, CDN_BASE_URL } from '../../config/const.config';
import ConfirmPopup from '../common/confirm-popup.component';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/hls-transcoder.config';
import Sortable from '../table-elements/sortable.component';
import Error from '../table-elements/error.component';
import ManageBuckets from './manage-buckets.component';
import HLSTranscoderFilesOverwrite from './hls-transcoder-files-overwrite.component';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListHLSTranscoderObject = () => {
  const { getSignal, isAbortError } = useAbortableRequest();
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [bucketConnected, setBucketConnected] = useState(false);
  const [connectingBucket, setConnectingBucket] = useState(false);
  const [options, setOptions] = useState({
    loading: false,
    error: false,
    rows: [],
    totalRows: 0,
    page: 0,
    rowsPerPage: 300,
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    selectedRows: [],
    selectAllRows: false,
    columns: LISTING_COLUMNS,
    actions: LISTING_ACTIONS,
    queuedItemCounters: {},
    appliedFilters: {
      search: { name: 'Search', value: '' },
    },
  });
  const [processing, setProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [anchorActionEl, setAnchorActionEl] = useState(null);
  const [openActions, setOpenActions] = useState({
    show: false,
    data: null,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [showManageBucket, setShowManageBucket] = useState(false);
  const [refreshBucketList, setRefreshBucketList] = useState(0);
  const [rowsToOverwrite, setRowsToOverwrite] = useState([]);
  const [showFilesOverwriteConfirmation, setShowFilesOverwriteConfirmation] =
    useState(false);
  const [searchText, setSearchText] = useState('');
  const debounceRef = useRef(null);

  const selectedRowsSet = useMemo(
    () => new Set(options.selectedRows.map((item) => item.id)),
    [options.selectedRows]
  );
  const filteredRowsList = useMemo(
    () => options.rows.filter((item) => item.status === CDN77_STATUS.NEW),
    [options.rows]
  );
  let hasManageBucketActionSuccess = false;

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const triggerSearch = (value) => {
    setOptions((prev) => ({
      ...prev,
      appliedFilters: {
        ...prev.appliedFilters,
        search: {
          ...prev.appliedFilters.search,
          value,
        },
      },
      page: 0,
      reloadCounter: prev.reloadCounter + 1,
      loading: true,
    }));
  };

  const debouncedSearch = useCallback((value) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      triggerSearch(value);
    }, 500);
  }, []);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleConnect = () => {
    setOptions({ ...options, loading: true });
    setConnectingBucket(true);

    connectS3Bucket(selectedBucket.id)
      .then((res) => {
        if (res.data.success) {
          setBucketConnected(true);
          setConnectingBucket(false);
          handleRefreshData();
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setOptions({ ...options, loading: false });
        setConnectingBucket(false);
      });
  };

  const handleDisconnect = () => {
    setSelectedBucket(null);
    setBucketConnected(false);
    setOptions({
      ...options,
      rows: [],
      selectedRows: [],
      selectAllRows: false,
    });
  };

  const checkFileStatusApiCall = async (fileId) => {
    try {
      const checkStatusResponse = await checkFileStatus(fileId);
      if (checkStatusResponse.data) {
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  status: checkStatusResponse.data.status,
                  isLoading: false,
                }
              : item
          ),
          selectedRows: prev.selectedRows.filter((item) => item.id !== fileId),
          selectAllRows: false,
        }));
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Something went wrong, please try again.';

      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'error',
        message,
      });
      setCheckingStatus(false);
    }
  };

  const handleCheckFileStatus = async () => {
    setCheckingStatus(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const checkFileList = options.rows.filter(
      (item) =>
        item.status !== CDN77_STATUS.COMPLETE &&
        item.status !== CDN77_STATUS.NEW
    );
    if (checkFileList.length === 0) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'info',
          message: 'No files available to check status.',
        });
      }, 100);
      setCheckingStatus(false);
      return;
    }

    for (let i = 0; i < checkFileList.length; i += 1) {
      setSnackbarInfo({ ...snackbarInfo, show: false });
      setOptions((prev) => ({
        ...prev,
        rows: prev.rows.map((item) => ({
          ...item,
          isLoading: checkFileList[i].id === item.id,
        })),
      }));

      await checkFileStatusApiCall(checkFileList[i].id);
    }

    setCheckingStatus(false);
  };

  const handleStartTranscoding = async () => {
    setProcessing(true);
    let queuedCnt = 0;
    const processOverwriteRows = [];

    for (let i = 0; i < options.selectedRows.length; i += 1) {
      try {
        setSnackbarInfo({ ...snackbarInfo, show: false });
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((item) => ({
            ...item,
            isLoading: options.selectedRows[i].id === item.id,
          })),
        }));

        const transcodeFilePayload = {
          file: options.selectedRows[i],
          overwriteConfirmation: false,
        };
        const transcodeResponse = await transcodeS3Files(
          selectedBucket.id,
          transcodeFilePayload
        );
        if (transcodeResponse.data?.overwriteConfirmation) {
          processOverwriteRows.push(options.selectedRows[i]);

          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === options.selectedRows[i].id
                ? {
                    ...options.selectedRows[i],
                    status: 'pending confirmation',
                    isLoading: false,
                  }
                : item
            ),
            selectedRows: prev.selectedRows.filter(
              (item) => item.id !== options.selectedRows[i].id
            ),
            selectAllRows: false,
          }));
        } else if (transcodeResponse.data) {
          queuedCnt += 1;
          const successCnt = queuedCnt;

          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === transcodeResponse.data.id
                ? { ...transcodeResponse.data, isLoading: false }
                : item
            ),
            selectedRows: prev.selectedRows.filter(
              (item) => item.id !== options.selectedRows[i].id
            ),
            selectAllRows: false,
          }));

          setTimeout(() => {
            setSnackbarInfo({
              ...snackbarInfo,
              show: true,
              type: 'info',
              message: `File ${successCnt} / ${options.selectedRows.length} added to the transcoding queue.`,
            });
          }, 50);
        }
      } catch (err) {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      }
    }

    if (processOverwriteRows.length > 0) {
      setRowsToOverwrite(processOverwriteRows);
      setShowFilesOverwriteConfirmation(true);
    }

    setProcessing(false);
  };

  const handleRowSelection = (r) => {
    const finalSelectedRows = selectedRowsSet.has(r.id)
      ? options.selectedRows.filter((row) => row.id !== r.id)
      : [...options.selectedRows, r];

    if (finalSelectedRows.length === filteredRowsList.length) {
      setOptions({
        ...options,
        selectedRows: finalSelectedRows,
        selectAllRows: true,
      });
    } else {
      setOptions({
        ...options,
        selectedRows: finalSelectedRows,
        selectAllRows: false,
      });
    }
  };

  const handleSelectAllChange = () => {
    const finalSelectedRows = !options.selectAllRows ? filteredRowsList : [];
    setOptions({
      ...options,
      selectedRows: finalSelectedRows,
      selectAllRows: !options.selectAllRows,
    });
  };

  const handleActionOpen = (e, selectedRow) => {
    setAnchorActionEl(e.currentTarget);
    setOpenActions({
      ...openActions,
      show: true,
      data: selectedRow,
    });
  };
  const handleActionClose = () => {
    setAnchorActionEl(null);
    setOpenActions({
      ...openActions,
      show: false,
      data: null,
    });
  };
  const handleActionSelect = async (selectedAction) => {
    if (selectedAction === ACTIONS.CHECK_STATUS.value) {
      setCheckingStatus(true);
      setSnackbarInfo({ ...snackbarInfo, show: false });
      setOptions((prev) => ({
        ...prev,
        rows: prev.rows.map((item) => ({
          ...item,
          isLoading: openActions.data.id === item.id,
        })),
      }));

      await checkFileStatusApiCall(openActions.data.id);

      setCheckingStatus(false);
    } else if (selectedAction === ACTIONS.COPY_URL.value) {
      setSnackbarInfo({ ...snackbarInfo, show: false });
      const folder = openActions.data.filePath
        .split('.')
        .slice(0, -1)
        .join('.');
      const url = `${CDN_BASE_URL}encoded/${folder}/cmaf.m3u8`;
      copy(url);
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'The video URL has been successfully copied.',
        });
      }, 50);
    } else {
      setDoAction({
        data: openActions.data || null,
        action: selectedAction || null,
      });
    }
    handleActionClose();
  };
  const handleActionCancel = () => {
    setDoAction({
      data: null,
      action: null,
    });
  };

  const handleManageBucketCancelAction = () => {
    setShowManageBucket(false);
    if (hasManageBucketActionSuccess) {
      handleDisconnect();
      setRefreshBucketList((prev) => prev + 1);
    }
  };
  const handleManageBucketSuccessAction = () => {
    hasManageBucketActionSuccess = true;
  };

  const handleCancelJob = () => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    cancelTranscodingJob(doAction.data.id)
      .then((res) => {
        if (res.data) {
          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === doAction.data.id
                ? { ...item, status: CDN77_STATUS.CANCELLED }
                : item
            ),
          }));
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'Transcoding job cancelled successfully.',
          });
        }
        handleActionCancel();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleProcessAgain = () => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    processAgainTranscoding(doAction.data.id)
      .then((res) => {
        if (res.data) {
          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === doAction.data.id
                ? {
                    ...item,
                    resolution: -1,
                    jobId: 0,
                    status: CDN77_STATUS.NEW,
                  }
                : item
            ),
          }));
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'File added to the transcoding queue.',
          });
        }
        handleActionCancel();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  useEffect(() => {
    const signal = getSignal();

    if (selectedBucket?.id) {
      const params = [];
      params.push(`page=${options.page + 1}`);
      params.push(`perPage=${options.rowsPerPage}`);
      if (options.sortBy && options.sortOrder) {
        params.push(`sortBy=${options.sortBy}`);
        params.push(`sortOrder=${options.sortOrder}`);
      }
      const filters = Object.keys(options.appliedFilters).reduce((acc, key) => {
        acc[key] = options.appliedFilters[key].value;
        return acc;
      }, {});
      if (filters.search) {
        params.push(`q=${encodeURIComponent(filters.search)}`);
      }

      const uri = '';
      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      getS3FilesListData(selectedBucket.id, uri + paramsQuery, signal)
        .then((res) => {
          if (!signal.aborted) {
            setOptions({
              ...options,
              loading: false,
              rows: res.data.rows,
              totalRows: res.data.totalRows,
              queuedItemCounters: res.data.queuedItemCounters,
              appliedFilters: options.appliedFilters || {},
              error: false,
            });
          }
        })
        .catch((error) => {
          if (isAbortError(error)) {
            return;
          }
          setOptions({
            ...options,
            loading: false,
            page: 0,
            totalRows: 0,
            rows: [],
            queuedItemCounters: {},
            error: true,
          });
        });
    }
  }, [options.reloadCounter]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, pt: 1, pb: 4 }}
      >
        <Box width="60%">
          <Box display="flex" alignItems="center">
            <Buckets
              id="buckets"
              name="buckets"
              label="Buckets"
              defaultValue={selectedBucket}
              onChange={(v) => setSelectedBucket(v)}
              sx={{ width: '30%' }}
              disabled={bucketConnected || options.loading}
              reloadData={refreshBucketList}
            />

            {!bucketConnected ? (
              <Button
                type="button"
                color="primary"
                variant="contained"
                startIcon={
                  connectingBucket && <Iconify icon="eos-icons:loading" />
                }
                sx={{ ml: 1 }}
                onClick={() => handleConnect()}
                disabled={
                  !selectedBucket ||
                  options.loading ||
                  processing ||
                  checkingStatus ||
                  connectingBucket
                }
              >
                {connectingBucket ? 'Connecting...' : 'Connect'}
              </Button>
            ) : (
              <Button
                type="button"
                color="primary"
                variant="contained"
                sx={{ ml: 1 }}
                onClick={() => handleDisconnect()}
                disabled={
                  !selectedBucket ||
                  options.loading ||
                  processing ||
                  checkingStatus
                }
              >
                Disconnect
              </Button>
            )}

            <Button
              type="button"
              color="primary"
              variant="contained"
              sx={{ ml: 1 }}
              onClick={() => setShowManageBucket(true)}
              disabled={
                connectingBucket ||
                options.loading ||
                processing ||
                checkingStatus
              }
            >
              Manage
            </Button>
          </Box>

          {!selectedBucket && (
            <Typography variant="body2" sx={{ mt: 2, color: 'grey' }}>
              Please select bucket
            </Typography>
          )}
          {selectedBucket && !bucketConnected && (
            <Typography variant="body2" sx={{ mt: 2, color: 'grey' }}>
              Please connect to bucket
            </Typography>
          )}
        </Box>

        {selectedBucket && (
          <Box display="flex">
            <Box width={260}>
              <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                Bucket: <strong>{selectedBucket.bucket}</strong>
              </Typography>
              {selectedBucket?.bucket === 'pnm-primary-assets-storage' && (
                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  File URL: <strong>{selectedBucket.fileUrl}</strong>
                </Typography>
              )}
            </Box>

            <Box width={260}>
              <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                Source Path: <strong>{selectedBucket.sourcePath}</strong>
              </Typography>
              {selectedBucket?.bucket === 'pnm-primary-assets-storage' && (
                <Typography variant="body2" mb={1} sx={{ fontSize: 12 }}>
                  Destination Path:{' '}
                  <strong>{selectedBucket.destinationPath}</strong>
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Stack>

      {selectedBucket && bucketConnected && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={options.selectAllRows}
                onChange={() => handleSelectAllChange()}
                disabled={
                  options.rows.length === 0 ||
                  processing ||
                  options.loading ||
                  checkingStatus ||
                  filteredRowsList.length === 0
                }
                sx={{ pl: 0 }}
              />
              <Typography variant="body2" color="text.secondary">
                Select All
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
            >
              <TextField
                id="search"
                name="search"
                label="Search"
                variant="filled"
                value={searchText}
                onChange={handleSearchChange}
                disabled={processing || options.loading || checkingStatus}
              />
              <Button
                type="button"
                color="primary"
                variant="contained"
                startIcon={
                  checkingStatus && <Iconify icon="eos-icons:loading" />
                }
                onClick={() => handleCheckFileStatus()}
                disabled={
                  options.rows.length === 0 ||
                  processing ||
                  options.loading ||
                  checkingStatus
                }
              >
                {!checkingStatus ? 'Check File Status' : 'Checking Status...'}
              </Button>
              <Button
                type="button"
                color="primary"
                variant="contained"
                startIcon={processing && <Iconify icon="eos-icons:loading" />}
                onClick={() => handleStartTranscoding()}
                disabled={
                  options.rows.length === 0 ||
                  options.selectedRows.length === 0 ||
                  processing ||
                  options.loading ||
                  checkingStatus
                }
              >
                {!processing ? 'Add To Queue' : 'Adding To Queue...'}
              </Button>
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={() => handleConnect()}
                disabled={processing || options.loading || checkingStatus}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={2.5}
            py={1}
          >
            <Typography variant="subtitle2" color="secondary">
              Active Item - {options.queuedItemCounters?.active || 0}
            </Typography>

            <Typography variant="subtitle2" color="secondary">
              Complete Item - {options.queuedItemCounters?.complete || 0}
            </Typography>
          </Stack>

          {processing && (
            <Box py={1}>
              <Typography variant="subtitle1" color="primary" fontSize={11}>
                We&apos;re processing the request. Please do not close the
                window and do not leave this page.
              </Typography>
            </Box>
          )}

          <Card component={Paper} sx={{ my: 1 }}>
            {options.totalRows > 0 && (
              <TablePagination
                component="div"
                count={options.totalRows}
                page={options.page}
                onPageChange={(e, newPage) => handlePageChange(newPage)}
                rowsPerPage={options.rowsPerPage}
                onRowsPerPageChange={(e) => {
                  handleRowsPerPageChange(Number(e.target.value));
                }}
                rowsPerPageOptions={[10, 25, 50, 100, 300]}
              />
            )}

            <TableContainer variant="outlined" square>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" />
                    {options.columns.map((c) => (
                      <TableCell
                        key={`col-${c.id}`}
                        align={c.align}
                        width={c.width}
                        sx={{ cursor: c.sortable ? 'pointer' : 'inherit' }}
                        onClick={() => {
                          if (c.sortable) {
                            if (c.dataKey === options.sortBy) {
                              if (options.sortOrder === '') {
                                handleSortingChange(c.dataKey, 'asc');
                              } else if (options.sortOrder === 'asc') {
                                handleSortingChange(c.dataKey, 'desc');
                              } else if (options.sortOrder === 'desc') {
                                handleSortingChange('', '');
                              }
                            } else {
                              handleSortingChange(c.dataKey, 'asc');
                            }
                          }
                        }}
                      >
                        <Stack direction="row" justifyContent={c.align}>
                          {c.sortable && (
                            <Sortable
                              dataKey={c.dataKey}
                              sortBy={options.sortBy}
                              sortOrder={options.sortOrder}
                            />
                          )}
                          {c.label}
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!options.loading &&
                    !options.error &&
                    options.totalRows > 0 &&
                    options.rows.map((r) => (
                      <TableRow key={`bucket-files-${r.id}`}>
                        <TableCell align="center">
                          {r.status === CDN77_STATUS.NEW && (
                            <Checkbox
                              checked={selectedRowsSet.has(r.id)}
                              onChange={() => handleRowSelection(r)}
                              disabled={processing || checkingStatus}
                              sx={{ p: 0 }}
                            />
                          )}
                          {r.status !== CDN77_STATUS.NEW &&
                            r.status !== 'pending confirmation' && (
                              <IconButton
                                size="small"
                                color="inherit"
                                onClick={(e) => handleActionOpen(e, r)}
                                disabled={processing || checkingStatus}
                              >
                                <Iconify icon="eva:more-vertical-fill" />
                              </IconButton>
                            )}
                        </TableCell>
                        {options.columns.map((c) => (
                          <TableCell
                            key={`data-col-${c.id}-${r[c.dataKey]}`}
                            align={c.align}
                            width={c.width}
                            variant="string"
                          >
                            {c.dataKey === 'status' &&
                              (r.isLoading ? (
                                <Iconify icon="line-md:loading-loop" />
                              ) : (
                                r[c.dataKey]
                              ))}
                            {c.dataKey === 'modifiedAt' &&
                              formatTimestamp(r[c.dataKey])}
                            {c.dataKey !== 'status' &&
                              c.dataKey !== 'modifiedAt' &&
                              (r[c.dataKey] || '-')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                  {!options.loading &&
                    !options.error &&
                    options.totalRows === 0 && (
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={options.columns.length + 1}
                        >
                          <NoData />
                        </TableCell>
                      </TableRow>
                    )}

                  {!options.loading &&
                    options.error &&
                    options.totalRows === 0 && (
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={options.columns.length + 1}
                        >
                          <Error />
                        </TableCell>
                      </TableRow>
                    )}

                  {options.loading && (
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={options.columns.length + 1}
                      >
                        <Loading />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {options.totalRows > 0 && (
              <TablePagination
                component="div"
                count={options.totalRows}
                page={options.page}
                onPageChange={(e, newPage) => handlePageChange(newPage)}
                rowsPerPage={options.rowsPerPage}
                onRowsPerPageChange={(e) => {
                  handleRowsPerPageChange(Number(e.target.value));
                }}
                rowsPerPageOptions={[10, 25, 50, 100, 300]}
              />
            )}
          </Card>
        </>
      )}

      {openActions.show && (
        <Menu
          id="actions-menu"
          anchorEl={anchorActionEl}
          open
          onClose={() => handleActionClose()}
          size="small"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {options.actions.map((a, index) => {
            let render = true;
            if (a.conditional) {
              render = a.conditional(openActions.data);
            }

            if (render) {
              return (
                <MenuItem
                  key={`mia-${a.action}-${index}`}
                  onClick={() => handleActionSelect(a.action)}
                >
                  <Typography variant="body2">{a.label}</Typography>
                </MenuItem>
              );
            }

            return null;
          })}
        </Menu>
      )}

      {showManageBucket && (
        <ManageBuckets
          title="Manage Buckets"
          onCancel={handleManageBucketCancelAction}
          onManageBucketActionSuccess={handleManageBucketSuccessAction}
        />
      )}

      {doAction.action === ACTIONS.CANCEL_JOB.value && (
        <ConfirmPopup
          title={`Cancel Job - ${doAction.data.filePath}`}
          message={`Do you want to cancel job of ${doAction.data.filePath} video? You can not undo this action!`}
          onCancel={() => handleActionCancel()}
          onSuccess={() => handleCancelJob()}
        />
      )}

      {doAction.action === ACTIONS.PROCESS_AGAIN.value && (
        <ConfirmPopup
          title={`Process Again - ${doAction.data.filePath}`}
          message={`Are you sure you want to process again ${doAction.data.filePath} video? You can not undo this action!`}
          onCancel={() => handleActionCancel()}
          onSuccess={() => handleProcessAgain()}
        />
      )}

      {showFilesOverwriteConfirmation && (
        <HLSTranscoderFilesOverwrite
          title="Confirm Files Overwrite"
          onCancel={() => {
            setShowFilesOverwriteConfirmation(false);
            handleRefreshData();
          }}
          rowsToOverwrite={rowsToOverwrite}
          bucketId={selectedBucket.id}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

export default ListHLSTranscoderObject;
